import { useEffect, useMemo, useRef, useState } from 'react';

import { Arrow, Ellipse, Line, Rect } from 'react-konva';

export interface Tools {
  tool: string;
  data: any;
  color: string;
  opacity: number;
  strokeWidth: number;
}

const useItemsDefinition = () => {
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const toolsToIgnore = ['select', 'text'];
  const [tool, setTool] = useState('select');
  const [activeToolData, setActiveToolData] = useState<any>([]);
  const [selectedId, setSelectedId] = useState(-1);
  const [tools, setTools] = useState<Tools[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const selectedRef = useRef([]);
  const defaultValues = useMemo(() => {
    if (tool === 'marker') {
      return {
        color: 'yellow',
        fill: 'rgb(194, 220, 243)',
        strokeWidth: 18,
        opacity: 0.6,
      };
    }
    return {
      color: 'rgb(150, 185, 218)',
      fill: 'rgb(194, 220, 243)',
      strokeWidth: 5,
      opacity: 1,
    };
  }, [tool]);
  const calculateAndSetMenuPosition = points => {
    let minX = points[0];
    let maxX = points[0];
    let minY = points[1];
    let maxY = points[1];

    for (let i = 2; i < points.length; i += 2) {
      const x = points[i];
      const y = points[i + 1];
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }

    // Calculate the midpoint of the bounding box
    const midX = (minX + maxX) / 2;
    setPosition({ x: midX, y: minY - 60 });
  };

  useEffect(() => {
    setActiveToolData([]);
    setSelectedId(-1);
  }, [tool]);

  const toolLogic = {
    marker: {
      start: e => {
        const pos = e.target.getStage().getPointerPosition();
        setActiveToolData([pos.x, pos.y, pos.x, pos.y]);
      },

      move: e => {
        const pos = e.target.getStage().getPointerPosition();
        setActiveToolData(val => [...val, pos.x, pos.y]);
      },
      end: () => {
        setTools((value: Tools[]) => [
          ...value,
          { tool, data: activeToolData, ...defaultValues, tension: 1 },
        ]);

        setActiveToolData([]);
      },
      render: data => {
        return (
          <Line
            key={JSON.stringify(data)}
            points={data.data}
            stroke={data.color}
            opacity={0.6}
            strokeWidth={data.strokeWidth}
            tension={0.3}
            draggable={data.index === selectedId}
            onDragStart={e => {
              const pos = e.target.getStage().getPointerPosition();
              setDragStartPos(pos);
              setIsDragging(true);
            }}
            onDragEnd={e => {
              const pos = e.target.getStage().getPointerPosition();
              const dx = pos.x - dragStartPos.x;
              const dy = pos.y - dragStartPos.y;

              // Calculate new points by applying the offset to all points
              const newPoints = data.data.map((point, index) => {
                if (index % 2 === 0) {
                  return point + dx; // Even indices are x-coordinates
                }
                return point + dy; // Odd indices are y-coordinates
              });
              const tmpTools = JSON.parse(JSON.stringify(tools));
              tmpTools[data.index].data = newPoints;
              setTools(tmpTools);

              calculateAndSetMenuPosition(newPoints);
              setIsDragging(false);
            }}
            onClick={e => {
              if (!isToolPresent(data.tool)) return;
              calculateAndSetMenuPosition(data.data);

              setSelectedId(data.index);
            }}
          />
        );
      },
    },
    pen: {
      start: e => {
        const pos = e.target.getStage().getPointerPosition();
        setActiveToolData([pos.x, pos.y, pos.x, pos.y]);
      },

      move: e => {
        const pos = e.target.getStage().getPointerPosition();
        setActiveToolData(val => [...val, pos.x, pos.y]);
      },
      end: () => {
        setTools((value: Tools[]) => [
          ...value,
          { tool, data: activeToolData, ...defaultValues, tension: 1 },
        ]);

        setActiveToolData([]);
      },
      render: data => {
        return (
          <Line
            key={JSON.stringify(data)}
            points={data.data}
            stroke={data.color}
            opacity={data.opacity}
            strokeWidth={data.strokeWidth}
            tension={0.3}
            draggable={data.index === selectedId}
            onDragStart={e => {
              const pos = e.target.getStage().getPointerPosition();
              setDragStartPos(pos);
              setIsDragging(true);
            }}
            onDragEnd={e => {
              const pos = e.target.getStage().getPointerPosition();
              const dx = pos.x - dragStartPos.x;
              const dy = pos.y - dragStartPos.y;

              // Calculate new points by applying the offset to all points
              const newPoints = data.data.map((point, index) => {
                if (index % 2 === 0) {
                  return point + dx; // Even indices are x-coordinates
                }
                return point + dy; // Odd indices are y-coordinates
              });
              const tmpTools = JSON.parse(JSON.stringify(tools));
              tmpTools[data.index].data = newPoints;
              setTools(tmpTools);

              calculateAndSetMenuPosition(newPoints);
              setIsDragging(false);
            }}
            onClick={e => {
              if (!isToolPresent(data.tool)) return;
              calculateAndSetMenuPosition(data.data);

              setSelectedId(data.index);
            }}
          />
        );
      },
    },
    arrow: {
      start: e => {
        const pos = e.target.getStage().getPointerPosition();
        setActiveToolData([pos.x, pos.y, pos.x, pos.y]);
      },

      move: e => {
        const pos = e.target.getStage().getPointerPosition();
        setActiveToolData(val => [val[0], val[1], pos.x, pos.y]);
      },
      end: () => {
        setTools((value: Tools[]) => [
          ...value,
          { tool, data: activeToolData, ...defaultValues, tension: 1 },
        ]);

        setActiveToolData([]);
      },
      render: data => {
        return (
          <Arrow
            key={JSON.stringify(data)}
            points={data.data}
            stroke={data.color}
            opacity={data.opacity}
            strokeWidth={data.strokeWidth}
            tension={0.3}
            draggable={data.index === selectedId}
            onDragStart={e => {
              const pos = e.target.getStage().getPointerPosition();
              setDragStartPos(pos);
              setIsDragging(true);
            }}
            onDragEnd={e => {
              const pos = e.target.getStage().getPointerPosition();
              const dx = pos.x - dragStartPos.x;
              const dy = pos.y - dragStartPos.y;

              // Calculate new points by applying the offset to all points
              const newPoints = data.data.map((point, index) => {
                if (index % 2 === 0) {
                  return point + dx; // Even indices are x-coordinates
                }
                return point + dy; // Odd indices are y-coordinates
              });
              const tmpTools = JSON.parse(JSON.stringify(tools));
              tmpTools[data.index].data = newPoints;
              setTools(tmpTools);

              calculateAndSetMenuPosition(newPoints);
              setIsDragging(false);
            }}
            onClick={e => {
              if (!isToolPresent(data.tool)) return;
              calculateAndSetMenuPosition(data.data);

              setSelectedId(data.index);
            }}
          />
        );
      },
    },

    line: {
      start: e => {
        const pos = e.target.getStage().getPointerPosition();
        setActiveToolData([pos.x, pos.y, pos.x, pos.y]);
      },

      move: e => {
        const pos = e.target.getStage().getPointerPosition();
        setActiveToolData(val => [val[0], val[1], pos.x, pos.y]);
      },
      end: () => {
        setTools((value: Tools[]) => [
          ...value,
          { tool, data: activeToolData, ...defaultValues, tension: 1 },
        ]);

        setActiveToolData([]);
      },
      render: data => {
        return (
          <Line
            key={JSON.stringify(data)}
            points={data.data}
            stroke={data.color}
            opacity={data.opacity}
            strokeWidth={data.strokeWidth}
            tension={0.3}
            draggable={data.index === selectedId}
            onDragStart={e => {
              const pos = e.target.getStage().getPointerPosition();
              setDragStartPos(pos);
              setIsDragging(true);
            }}
            onDragEnd={e => {
              const pos = e.target.getStage().getPointerPosition();
              const dx = pos.x - dragStartPos.x;
              const dy = pos.y - dragStartPos.y;

              // Calculate new points by applying the offset to all points
              const newPoints = data.data.map((point, index) => {
                if (index % 2 === 0) {
                  return point + dx; // Even indices are x-coordinates
                }
                return point + dy; // Odd indices are y-coordinates
              });
              const tmpTools = JSON.parse(JSON.stringify(tools));
              tmpTools[data.index].data = newPoints;
              setTools(tmpTools);

              calculateAndSetMenuPosition(newPoints);
              setIsDragging(false);
            }}
            onClick={e => {
              if (!isToolPresent(data.tool)) return;
              calculateAndSetMenuPosition(data.data);

              setSelectedId(data.index);
            }}
          />
        );
      },
    },
    ellipse: {
      start: e => {
        const pos = e.target.getStage().getPointerPosition();
        setActiveToolData({ x: pos.x, y: pos.y, width: 0, height: 0 });
      },
      move: e => {
        const pos = e.target.getStage().getPointerPosition();
        const lastRect = JSON.parse(JSON.stringify(activeToolData));
        lastRect.width = pos.x - lastRect.x;
        lastRect.height = pos.y - lastRect.y;
        setActiveToolData(lastRect);
      },
      end: () => {
        setTools((value: Tools[]) => [...value, { tool, data: activeToolData, ...defaultValues }]);

        setActiveToolData([]);
      },
      render: data => {
        const index = data.index;
        return (
          <Ellipse
            key={JSON.stringify(data)}
            x={data.data.x}
            y={data.data.y}
            radiusX={data.data.width}
            radiusY={data.data.height}
            stroke={data.color}
            strokeWidth={data.strokeWidth}
            draggable={index === selectedId}
            onDragStart={e => {
              setIsDragging(true);
            }}
            onDragEnd={e => {
              const tmpTools = JSON.parse(JSON.stringify(tools));
              const lastRect = tmpTools[data.index];
              const pos = e.target;
              lastRect.data.x = pos.x();
              lastRect.data.y = pos.y();
              tmpTools[data.index] = lastRect;
              setTools(tmpTools);
              setPosition({
                x: e.target.x() + tools[data.index].data.width / 4,
                y: e.target.y() - data.data.height - 60,
              });

              setIsDragging(false);
            }}
            fill={data.fill ?? 'transparent'}
            onClick={e => {
              if (!isToolPresent(data.tool)) return;
              setPosition({
                x: data.data.x + data.data.width / 4,
                y: data.data.y - data.data.height - 60,
              });
              setSelectedId(index);
            }}
          />
        );
      },
    },
    rect: {
      start: e => {
        const pos = e.target.getStage().getPointerPosition();
        setActiveToolData({ x: pos.x, y: pos.y, width: 0, height: 0 });
      },
      move: e => {
        const pos = e.target.getStage().getPointerPosition();
        const lastRect = JSON.parse(JSON.stringify(activeToolData));
        lastRect.width = pos.x - lastRect.x;
        lastRect.height = pos.y - lastRect.y;
        setActiveToolData(lastRect);
      },
      end: () => {
        setTools((value: Tools[]) => [...value, { tool, data: activeToolData, ...defaultValues }]);

        setActiveToolData([]);
      },
      render: data => {
        const index = data.index;
        return (
          <Rect
            key={JSON.stringify(data)}
            x={data.data.x}
            y={data.data.y}
            width={data.data.width}
            height={data.data.height}
            stroke={data.color}
            strokeWidth={data.strokeWidth}
            draggable={index === selectedId}
            onDragStart={e => {
              setIsDragging(true);
            }}
            onDragEnd={e => {
              const tmpTools = JSON.parse(JSON.stringify(tools));
              const lastRect = tmpTools[data.index];
              const pos = e.target;
              lastRect.data.x = pos.x();
              lastRect.data.y = pos.y();
              tmpTools[data.index] = lastRect;
              setTools(tmpTools);
              setPosition({
                x: e.target.x() + tools[data.index].data.width / 2,
                y: e.target.y() - 60,
              });

              setIsDragging(false);
            }}
            fill={data.fill ?? 'transparent'}
            onClick={e => {
              if (!isToolPresent(data.tool)) return;
              setPosition({ x: data.data.x + data.data.width / 2, y: data.data.y - 60 });
              setSelectedId(index);
            }}
          />
        );
      },
    },
  };

  const isToolPresent = toolLocal => toolLocal in toolLogic;

  const renderTool = (toolLocal, data) => {
    if (!isToolPresent(toolLocal)) return;
    return toolLogic[toolLocal].render(data);
  };

  const handleMouseDownForTool = (toolLocal, data) => {
    if (!isToolPresent(toolLocal)) return;
    toolLogic[toolLocal].start(data);
  };
  const handleMouseMoveForTool = (toolLocal, data) => {
    if (!isToolPresent(toolLocal)) return;
    toolLogic[toolLocal].move(data);
  };
  const handleMouseUpForTool = (toolLocal, data?) => {
    if (!isToolPresent(toolLocal) || activeToolData.lenght === 0) return;
    toolLogic[toolLocal].end();
  };
  return {
    isDragging,
    selectedRef,
    handleMouseDownForTool,
    handleMouseMoveForTool,
    handleMouseUpForTool,
    position,
    selectedId,
    setTools,
    tools,
    renderTool,
    defaultValues,
    activeToolData,
    tool,
    setTool,
    toolsToIgnore,
    setSelectedId,
  };
};

export default useItemsDefinition;
